/* @flow */

import type { GamestreamToken } from '../../../helpers/gaming/types';
import type { BasicFunction, KeyValuePair } from '../../../helpers/jsHelpers/types';
import type { VOD_PURCHASE_DATA_TYPE } from '../../../helpers/rights/pendingOperations';
import type { VOD_STATUS_TYPE } from '../../../helpers/ui/metadata/Types';
import type { ChannelMap } from '../../../libs/netgemLibrary/v8/types/Channel';
import type { NETGEM_API_V8_METADATA_PROGRAM, NETGEM_API_V8_METADATA_SERIES } from '../../../libs/netgemLibrary/v8/types/MetadataProgram';
import type { NETGEM_API_V8_METADATA_SCHEDULE } from '../../../libs/netgemLibrary/v8/types/MetadataSchedule';
import type { NETGEM_API_PLAYINGHISTORY_ITEM, PLAYING_HISTORY_ARRAY_TYPE } from '../../../libs/netgemLibrary/v8/types/PlayingHistory';
import type { NETGEM_API_V8_PURCHASE_INFO } from '../../../libs/netgemLibrary/v8/types/PurchaseInfo';
import type { NETGEM_API_V8_AUTHENT_REALM } from '../../../libs/netgemLibrary/v8/types/Realm';
import type { NETGEM_API_V8_RIGHTS } from '../../../libs/netgemLibrary/v8/types/Rights';
import type { NETGEM_API_VIEWINGHISTORY } from '../../../libs/netgemLibrary/v8/types/ViewingHistory';
import type { AppConfigurationState } from '../../../redux/appConf/reducers';
import type { BO_PURCHASE_LIST_TYPE } from '../../../redux/netgemApi/actions/videofutur/types/purchase';
import type { CONFIRMATION_DATA_MODAL_TYPE } from '../../modal/confirmationModal/ConfirmationModal';
import type { EULA_DATA_MODAL_TYPE } from '../../modal/eulaModal/EulaModal';
import type { GAME_PROFILE_SELECT_DATA_MODAL_TYPE } from '../../modal/gameProfileSelectModal/GameProfileSelectModal';
import type { MESSAGE_MODAL_TYPE } from '../../modal/messageModal/MessageModal';

import { LoadableStatus } from '../../../helpers/loadable/loadable';
import { BroadcastStatus } from '../../../helpers/ui/location/Format';
import ContinuousWatching from '../../../helpers/viewingHistory/ContinuousWatching';
import { ItemContent, type NETGEM_API_V8_FEED_ITEM } from '../../../libs/netgemLibrary/v8/types/FeedItem';
import { type NetgemApiV8WidgetItemClick, type TILE_CONFIG_TYPE, TileConfig } from '../../../libs/netgemLibrary/v8/types/WidgetConfig';
import { MetadataStatus } from './ItemConstsAndTypes';
import { ItemOverlayView } from './ItemOverlay';

export enum TruncateState {
  Initializing,
  NotTruncated,
  Truncated,
}

export type ReduxItemOverlayDispatchToPropsType = {
  +localGetGamestreamToken: (item: NETGEM_API_V8_FEED_ITEM, signal?: AbortSignal) => Promise<GamestreamToken>,
  +localHideModal: BasicFunction,
  +localShowConfirmation: (data: CONFIRMATION_DATA_MODAL_TYPE, ariaLabel: string) => void,
  +localShowEula: (data: EULA_DATA_MODAL_TYPE) => void,
  +localShowGameProfileSelect: (data: GAME_PROFILE_SELECT_DATA_MODAL_TYPE) => void,
  +localShowMessage: (messageData: MESSAGE_MODAL_TYPE, ariaLabel: string) => void,
  +localUpdatePlayingHistory: (playingHistoryItem: NETGEM_API_PLAYINGHISTORY_ITEM, itemId: string) => Promise<void>,
  +showVodPurchaseDialog: (vodPurchaseData: VOD_PURCHASE_DATA_TYPE) => void,
};

export type ReduxItemOverlayReducerStateType = {
  +appConfiguration: AppConfigurationState,
  +channels: ChannelMap,
  +commercialOffers: KeyValuePair<Array<string>> | null,
  +defaultOnItemClick: NetgemApiV8WidgetItemClick | null,
  +defaultRights: NETGEM_API_V8_RIGHTS | null,
  +isRegisteredAsGuest: boolean,
  +isSubscriptionFeatureEnabled: boolean,
  +playingHistory: PLAYING_HISTORY_ARRAY_TYPE,
  +playingHistoryStatus: LoadableStatus,
  +purchaseList: BO_PURCHASE_LIST_TYPE | null,
  +userRights: Array<string> | null,
  +viewingHistory: NETGEM_API_VIEWINGHISTORY,
  +viewingHistoryStatus: LoadableStatus,
};

export type ItemOverlayPropType = {
  +authority?: NETGEM_API_V8_AUTHENT_REALM,
  +broadcastStatus: BroadcastStatus,
  +contentType: ItemContent,
  +distributorId: string | null,
  +gameLocationMetadata: NETGEM_API_V8_METADATA_SCHEDULE | null,
  +hoverContent: Set<TileConfig>,
  +innerRef?: ((ItemOverlayView | null) => unknown) | null,
  +isInLiveSection?: boolean,
  +isVisible: boolean,
  +item: NETGEM_API_V8_FEED_ITEM,
  +now: number,
  +onClick: (event: SyntheticMouseEvent<HTMLElement> | SyntheticTouchEvent<HTMLElement>) => void,
  +onItemClick?: NetgemApiV8WidgetItemClick,
  +previewCatchupScheduledEventId: string | null,
  +programMetadata: NETGEM_API_V8_METADATA_PROGRAM | null,
  +programMetadataStatus: MetadataStatus,
  +programTitle: string,
  +purchaseInfo: NETGEM_API_V8_PURCHASE_INFO | null,
  +seriesMetadata: NETGEM_API_V8_METADATA_SERIES | null,
  +seriesMetadataStatus: MetadataStatus,
  +seriesTitle: string,
  +tileConfig: TILE_CONFIG_TYPE,
  +tvLocationMetadata: NETGEM_API_V8_METADATA_SCHEDULE | null,
  +viewingHistoryId?: string | null,
  +vodLocationsMetadata: Array<NETGEM_API_V8_METADATA_SCHEDULE> | null,
  +vodStatus: VOD_STATUS_TYPE | null,
  +vtiId: ?number,
};

export type CompleteItemOverlayPropType = {
  ...ItemOverlayPropType,
  ...ReduxItemOverlayReducerStateType,
  ...ReduxItemOverlayDispatchToPropsType,
};

export type ItemOverlayStateType = {
  continuousWatching: ?ContinuousWatching,
  continuousWatchingButtonText: string,
  isLoading: boolean,
  synopsis: string | null,
  textHeight: number,
  truncateState: TruncateState,
};
